import { TariffComponent as TariffComponentType } from 'subdomains/cpo/types/charging-policy';
import { TFunction } from 'react-i18next';
import { convertSecondsToHHMM } from 'utils/date';

export function formatTimeOfDayRestriction(
  timeOfDay: NonNullable<TariffComponentType['restrictions']>['timeOfDay'],
  t: TFunction<'translation', undefined>
): string {
  if (!timeOfDay?.enabled || (!timeOfDay.start && !timeOfDay.end)) return '';

  if (timeOfDay.start && timeOfDay.end) {
    return `${convertSecondsToHHMM(timeOfDay.start)}-${convertSecondsToHHMM(timeOfDay.end)}`;
  } else if (timeOfDay.start) {
    return t('tariff.component.previewTimeOfDayFrom', 'From {{start}}', {
      start: convertSecondsToHHMM(timeOfDay.start),
    });
  } else if (timeOfDay.end) {
    return t('tariff.component.previewTimeOfDayUntil', 'Until {{end}}', {
      end: convertSecondsToHHMM(timeOfDay.end),
    });
  }
  return '';
}

export function formatDurationRestriction(
  duration: NonNullable<TariffComponentType['restrictions']>['duration'],
  t: TFunction<'translation', undefined>
): string {
  if (!duration?.enabled || (!duration.min && !duration.max)) return '';

  const unit =
    duration.unit === 'HOUR'
      ? t('tariff.component.previewDurationRangeHourUnit', 'h')
      : t('tariff.component.previewDurationRangeHourMinute', 'min');
  if (duration.min && duration.max) {
    return t(
      'tariff.component.previewDurationRange',
      '{{min}}{{unit}}-{{max}}{{unit}}',
      {
        min: duration.min,
        max: duration.max,
        unit,
      }
    );
  } else if (duration.min) {
    return t('tariff.component.previewDurationMin', '>{{min}}{{unit}}', {
      min: duration.min,
      unit,
    });
  } else if (duration.max) {
    return t('tariff.component.previewDurationMax', '<{{max}}{{unit}}', {
      max: duration.max,
      unit,
    });
  }
  return '';
}

export function formatDaysOfWeekRestriction(
  daysOfWeek: NonNullable<TariffComponentType['restrictions']>['daysOfWeek']
): string {
  if (!daysOfWeek?.enabled || daysOfWeek.values.length === 0) return '';

  const dayAbbreviations: { [key: string]: string } = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };
  return daysOfWeek.values
    .map((day: string) => dayAbbreviations[day])
    .join(',');
}

export function formatDateRestriction(
  date: NonNullable<TariffComponentType['restrictions']>['date']
): string {
  if (!date?.enabled || (!date.start && !date.end)) return '';

  if (date.start && date.end) {
    return `${formatDateForDisplay(date.start)}-${formatDateForDisplay(date.end)}`;
  } else if (date.start) {
    return `From ${formatDateForDisplay(date.start)}`;
  } else if (date.end) {
    return `Until ${formatDateForDisplay(date.end)}`;
  }
  return '';
}

export function formatEnergyRestriction(
  energy: NonNullable<TariffComponentType['restrictions']>['energy'],
  t: TFunction<'translation', undefined>
): string {
  if (!energy?.enabled || (!energy.min && !energy.max)) return '';

  if (energy.min && energy.max) {
    return t('tariff.component.previewEnergyRange', '{{min}}-{{max}}kWh', {
      min: energy.min,
      max: energy.max,
    });
  } else if (energy.min) {
    return t('tariff.component.previewEnergyMin', '>{{min}}kWh', {
      min: energy.min,
    });
  } else if (energy.max) {
    return t('tariff.component.previewEnergyMax', '<{{max}}kWh', {
      max: energy.max,
    });
  }
  return '';
}

export function formatDateForDisplay(date: Date): string {
  if (!date || isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}
